/**
 * Format Composable
 * 數字格式化工具
 *
 * 架構：
 * - 單例模式：使用 nuxtApp._formatContext 避免 storeToRefs 重複創建
 */

import numbro from 'numbro'
import { storeToRefs } from 'pinia'

import { useSiteConfigStore } from '@/stores/siteConfig'

const NUMBER_FORMATS = {
  INTEGER: {
    thousandSeparated: true,
    mantissa: 0,
  },
  FORCE_FLOAT: {
    thousandSeparated: true,
    roundingFunction: Math.floor,
    mantissa: 2,
  },
  FLOAT: {
    thousandSeparated: true,
    roundingFunction: Math.ceil,
    trimMantissa: true,
    mantissa: 2,
  },
  PERCENT: {
    trimMantissa: true,
    mantissa: 3,
  },
  HUMAN: {
    roundingFunction: Math.floor,
    trimMantissa: true,
    average: true,
    mantissa: 2,
  },
  RANK: {
    roundingFunction: Math.floor,
    average: true,
    mantissa: 2,
  },
  JACKPOT: {
    thousandSeparated: true,
    roundingFunction: Math.floor,
    mantissa: 2,
  },
  REWARD: {
    trimMantissa: true,
    average: true,
    mantissa: 3,
  },
  CRYPTO: {
    roundingFunction: Math.floor,
    thousandSeparated: true,
    trimMantissa: true,
    mantissa: 10,
  },
  CRYPTO_WAY: {
    roundingFunction: Math.floor,
    thousandSeparated: true,
    trimMantissa: true,
    mantissa: 4,
  },
  POINTS: {
    thousandSeparated: true,
    roundingFunction: Math.floor,
    trimMantissa: true,
    average: true,
    mantissa: 3,
  },
}

export const useFormat = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 storeToRefs 只創建一次，避免 SSR 環境下記憶體洩漏
  if (!nuxtApp._formatContext) {
    const siteConfigStore = useSiteConfigStore()
    const { balancePrecision } = storeToRefs(siteConfigStore)

    const getFormatted = (data, formatName) => {
      const parseData = data || 0
      const format = NUMBER_FORMATS[formatName] ? { ...NUMBER_FORMATS[formatName] } : formatName

      if (numbro.unformat(parseData) === undefined) {
        return parseData
      }

      if (format === undefined) {
        return numbro(parseData).format({ thousandSeparated: true })
      }

      if (formatName === 'FORCE_FLOAT' || formatName === 'FLOAT') {
        format.mantissa = parseInt(balancePrecision.value) || 2
      }

      if (!!format?.average && format?.average === true) {
        return numbro(parseData)
          .format({
            ...format,
            average: formatName === 'POINTS' ? parseData >= 10000 : parseData >= 1000,
          })
          .toUpperCase()
      } else {
        return numbro(parseData).format(format)
      }
    }

    // cleanup 方法：供 SSR cleanup plugin 調用（此 composable 無 watch 需清理）
    const cleanup = () => {}

    nuxtApp._formatContext = {
      getFormatted,
      cleanup,
    }
  }

  return nuxtApp._formatContext
}

/**
 * 將小數轉換為百分比字串，修正 JS 浮點運算精度問題
 * @param {number|string} value - 小數值（例：0.011）
 * @param {number} precision - 小數位數上限，預設 2
 * @returns {string} 百分比字串（例：'1.1%'），非數字輸入回傳 '0%'
 */
export const decimalToPercent = (value, precision = 2) => {
  const num = parseFloat(value)
  if (isNaN(num)) return '0%'
  return `${parseFloat((num * 100).toFixed(precision))}%`
}
